import { useState } from "react";
import { useRecordEvent } from "app/hook";
import { AnalyticsEventType } from "app/types";
import { ParentGate } from "features/parent/ParentGate";
import { PlanSubTab } from "features/user/page/UserPageTab";
import { AVAILABLE_UPGRADE_PLANS } from "features/user/constants";
import { PlanCategory, BillingPeriod, usePlan, useUpgradePurchase } from "features/subscription";
import {
  UserPlanCurrentUsageCard,
  UserPlanUpgradePreviewCard,
  UserPlanUpgradeCta,
  UserPlanInactiveNotice,
  UserPlanWarningDialogs,
  GuestPurchaseOverlay,
} from "./components";

type Props = {
  subTab: PlanSubTab;
};

export const UserPlan = ({ subTab }: Props) => {
  const plan = usePlan();
  const upgrade = useUpgradePurchase();
  const handleRecordEvent = useRecordEvent();

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const [parentGateVisible, setParentGateVisible] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<PlanCategory | null>(null);

  const availablePlans = AVAILABLE_UPGRADE_PLANS[plan] ?? [];
  const showCta = availablePlans.length > 0;

  const handleUpgradeClick = (category: PlanCategory) => {
    handleRecordEvent(AnalyticsEventType.UPGRADE_PLAN_SELECT, {
      plan_category: category,
      billing_period: billingPeriod,
    });
    setPendingPurchase(category);
    setParentGateVisible(true);
  };

  const handleParentGateConfirm = () => {
    setParentGateVisible(false);
    if (pendingPurchase) {
      upgrade.purchase(pendingPurchase, billingPeriod, "user_plan");
      setPendingPurchase(null);
    }
  };

  const handleParentGateClose = () => {
    setParentGateVisible(false);
    setPendingPurchase(null);
  };

  const getPriceString = (category: PlanCategory, period: BillingPeriod): string =>
    upgrade.getPriceDisplay(category, period).priceString;

  const showPurchaseTab = subTab === PlanSubTab.PURCHASE && showCta;

  return (
    <div className="flex-col-el w-full h-full overflow-hidden">
      <div className="flex-col-el flex-1 min-h-0 w-full overflow-hidden">
        {showPurchaseTab ? (
          <GuestPurchaseOverlay>
            <div className="flex-col-el flex-1 min-h-0 w-full overflow-hidden px-4 pb-4 pt-2 gap-3">
              <UserPlanUpgradeCta
                currentPlan={plan}
                availablePlans={availablePlans}
                billingPeriod={billingPeriod}
                getPriceString={getPriceString}
                onChangeBillingPeriod={setBillingPeriod}
                onUpgrade={handleUpgradeClick}
                includeFreePlan
              />
            </div>
          </GuestPurchaseOverlay>
        ) : (
          <div className="flex-col-el flex-1 min-h-0 w-full overflow-hidden px-4 pb-4 pt-2 gap-3">
            {showCta ? (
              <div className="flex-row-el flex-1 min-h-0 gap-3 w-full">
                <div className="flex-col-el flex-[2] min-h-0 min-w-0">
                  <UserPlanCurrentUsageCard />
                </div>
                <div className="flex-col-el flex-[3] min-h-0 min-w-0">
                  <UserPlanUpgradePreviewCard availablePlans={availablePlans} />
                </div>
              </div>
            ) : (
              <>
                <UserPlanCurrentUsageCard />
                <UserPlanInactiveNotice plan={plan} />
              </>
            )}
          </div>
        )}

        <ParentGate
          visible={parentGateVisible}
          confirm={handleParentGateConfirm}
          close={handleParentGateClose}
        />

        <UserPlanWarningDialogs
          showVersionWarning={upgrade.showVersionWarning}
          onCloseVersionWarning={upgrade.closeVersionWarning}
          onGotoStoreUpdate={upgrade.gotoStoreUpdate}
        />
      </div>
    </div>
  );
};
